import GeneralModule from "../modules/general";
import PagesModule from "../modules/pages";
import BlocksModule from "../modules/blocks";
import WishlistModule from "../modules/wishlist";
import CartModule from "../modules/cart";
import AuthModule from "../modules/auth";
import CatalogModule from "../modules/catalog";
import OrdersModule from "../modules/orders";
import CollectionsModule from "../modules/collections";
import EntitiesModule from "../modules/entities";
import MaterialsModule from "../modules/materials";
import ProductsModule from "../modules/products";
import ReviewsModule from "../modules/reviews";
import UserModule from "../modules/user";
import CategoriesModule from "../modules/categories";
import EventsModule from "../modules/events";
import BlogModule from "../modules/blog";
import SearchModule from "../modules/search";
import CheckoutModule from "../modules/checkout";

import type { FetchContext, FetchOptions } from "ofetch";
import {$Fetch, NitroFetchRequest} from "nitropack";
import {Device} from "@nuxtjs/device/runtime/types";

export interface IApiInstance {
    general: GeneralModule;
    pages: PagesModule;
    blocks: BlocksModule;
    wishlist: WishlistModule;
    cart: CartModule;
    auth: AuthModule;
    catalog: CatalogModule;
    orders: OrdersModule;
    collections: CollectionsModule;
    entities: EntitiesModule;
    materials: MaterialsModule;
    products: ProductsModule;
    reviews: ReviewsModule;
    user: UserModule;
    categories: CategoriesModule;
    events: EventsModule;
    blog: BlogModule;
    search: SearchModule;
    checkout: CheckoutModule;
}

type onResponseErrorType = (context: FetchContext) => void;

interface ICurrentTranslation<T extends Record<string, any>> {
    currentTranslation?: T["translations"][number];
}

type AddCurrentTranslation = {
    <T>(
        data: T[],
        translationId: number,
        defaultTranslationId: number,
    ): T & { currentTranslation?: ICurrentTranslation<T & Record<string, any>> }[];
    <T>(
        data: T,
        translationId: number,
        defaultTranslationId: number,
    ): T & { currentTranslation?: ICurrentTranslation<T & Record<string, any>> };
    (
        data: any,
        translationId: number,
        defaultTranslationId: number,
    ): any
};

type SetConfigType = () => {
    apiBase: string;
    onResponseError: onResponseErrorType;
    cacheVersions: Record<string, number> | null;
    defaultLangCode: string | undefined;
    authToken: string | null;
    currentLanguageId: number | undefined;
    defaultLanguageId: number | undefined;
    addCurrentTranslation: AddCurrentTranslation;
    currentLanguageCode: string;
    isDesktop: boolean;
    uid: string;
};



export class ApiInstance {
    private apiFetcher: $Fetch<unknown, NitroFetchRequest>;
    private fetchOptions: FetchOptions;
    public modules: IApiInstance;
    private apiBase: string;
    private onResponseError: onResponseErrorType;
    private cacheVersions: Record<string, number> | null;
    private defaultLangCode: string;
    private authToken: string | null;
    private currentLanguageId: number | undefined;
    private defaultLanguageId: number | null;
    private addCurrentTranslation: AddCurrentTranslation;
    private isDesktop: boolean;
    private uid: string;
    private readonly setConfig: SetConfigType;
    private currentLanguageCode: string;
    // private fatalErrors: number[];
    constructor(setConfig: SetConfigType) {
        this.authToken = "";
        this.apiBase = ""
        this.cacheVersions = {}
        this.addCurrentTranslation = () => {}
        this.currentLanguageCode = ""
        this.defaultLangCode = ""
        this.defaultLanguageId = null
        this.isDesktop = true
        this.uid = ''
        this.onResponseError = () => {}

        this.setConfig = setConfig;
        this.updateData();


        this.fetchOptions = {
            baseURL: this.apiBase,
            onRequest: (requestData) => {
                this.updateData();
                this.updateCacheVersionParam(requestData, this.cacheVersions);
                this.setRequestHeaders(requestData, this.defaultLangCode, this.authToken);
            },
            onResponseError: (context) => {
                this.onResponseError(context);
                // if (
                //     !context?.options?.skipErrors?.includes(context.response.status) &&
                //     this.fatalErrors.includes(context.response.status)
                // ) {
                //     const nuxtApp = useNuxtApp();
                //     const generalStore = useGeneralStore();
                //     const localePath = useLocalePathPolyfill();
                //     nuxtApp.runWithContext(() => {
                //         if (context.response.status === 401) {
                //             generalStore.setUserInfo(null, false);
                //             return navigateTo(localePath("/"));
                //         }
                //         return showError({
                //             statusCode: context.response.status,
                //             statusMessage:
                //                 context.response?._data?.errors?.[0]?.message || "Error",
                //             fatal: true,
                //         });
                //     });
                // }
            },
            onResponse: (context) => {
                const currentTranslationId =
                    this.currentLanguageId as number ||
                    this.defaultLanguageId as number;

                context.response._data = {
                    ...this.addCurrentTranslation(
                        context.response._data,
                        currentTranslationId,
                        this.defaultLanguageId as number,
                    ),
                    fetchedAt: new Date(),
                };
                return context.response._data
            },
        };
        this.apiFetcher = $fetch.create(this.fetchOptions);
        this.modules = {
            general: new GeneralModule(this.apiFetcher),
            pages: new PagesModule(this.apiFetcher),
            blocks: new BlocksModule(this.apiFetcher),
            wishlist: new WishlistModule(this.apiFetcher),
            cart: new CartModule(this.apiFetcher),
            auth: new AuthModule(this.apiFetcher),
            catalog: new CatalogModule(this.apiFetcher),
            orders: new OrdersModule(this.apiFetcher),
            collections: new CollectionsModule(this.apiFetcher),
            entities: new EntitiesModule(this.apiFetcher),
            materials: new MaterialsModule(this.apiFetcher),
            products: new ProductsModule(this.apiFetcher),
            reviews: new ReviewsModule(this.apiFetcher),
            user: new UserModule(this.apiFetcher),
            categories: new CategoriesModule(this.apiFetcher),
            events: new EventsModule(this.apiFetcher),
            blog: new BlogModule(this.apiFetcher),
            search: new SearchModule(this.apiFetcher),
            checkout: new CheckoutModule(this.apiFetcher),
        };

    }

    updateCacheVersionParam = (
        requestData: FetchContext,
        cacheVersions: ReturnType<any>, // TODO
    ) => {
        type CacheApiEndpointsKeys = keyof typeof CACHE_API_ENDPOINTS;
        const slug = (requestData.request as string).split(
            "/",
        )?.[1] as CacheApiEndpointsKeys;

        const isMethodGet =
            requestData.options?.method?.toLowerCase() === "get" ||
            !requestData.options?.method;
        if (
            isMethodGet &&
            slug &&
            Object.keys(CACHE_API_ENDPOINTS).includes(slug)
        ) {
            const param = CACHE_API_ENDPOINTS?.[slug];
            let queries = {};

            if (requestData?.options?.query) {
                queries = {
                    ...requestData.options.query,
                };
            }
            requestData.options.query = {
                ...queries,
                cv: cacheVersions?.[param] || undefined,
            };
        }
    }

    setRequestHeaders(
        requestData: FetchContext,
        defaultLangCode: string,
        authToken: string | null,
    ) {
        const headers: { [key: string]: any } = {
            "User-Device": this.isDesktop ? 1 : 2,
            "Accept-Language": this.currentLanguageCode,
        };
        let token = "";
        if (authToken) {
            token = authToken;
        }

        if (token) {
            headers.authorization = `Bearer ${token}`;
        }



        if (this.uid) {
            headers.uid = this.uid;
        }


        let lang = this.currentLanguageCode;

        if (defaultLangCode && defaultLangCode !== this.currentLanguageCode) {
            lang = `${this.currentLanguageCode},${defaultLangCode}`;
        }
        headers.lang = lang;
        requestData.options.headers = headers as Headers;
        requestData.options.query = {
           ...requestData.options.query,
           lang
        }
    }

    updateData() {
        const {
            apiBase,
            onResponseError,
            cacheVersions,
            defaultLangCode,
            authToken,
            currentLanguageId,
            defaultLanguageId,
            addCurrentTranslation,
            currentLanguageCode,
            isDesktop,
            uid
        } = this.setConfig();
        this.apiBase = apiBase;
        this.onResponseError = onResponseError;
        this.cacheVersions = cacheVersions
        this.defaultLangCode = defaultLangCode as string
        this.authToken = authToken
        this.currentLanguageId = currentLanguageId
        this.defaultLanguageId = defaultLanguageId as number
        this.addCurrentTranslation = addCurrentTranslation
        this.currentLanguageCode = currentLanguageCode
        this.isDesktop = isDesktop
        this.uid = uid
    }

}
